// ─────────────────────────────────────────────
//  src/hooks/usePortfolio.js
//  Phase 5: Full portfolio hook
//  • Holdings + transactions from Firestore
//  • Live CMP via fetchBatchQuotes
//  • Add/remove/update holdings
//  • Add transactions (BUY/SELL) with avg price recalc
//  • Batch import from parsed Excel rows
// ─────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import { db } from "../config/firebaseconfig";
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, updateDoc, writeBatch, getDocs, serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { fetchBatchQuotes } from "../config/api";

export function usePortfolio() {
  const { user } = useAuth();
  const [holdings,     setHoldings]     = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [liveData,     setLiveData]     = useState({});
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  // ── 1. Listen to Firestore holdings ──────────────────────
  useEffect(() => {
    if (!user) return;
    const ref  = collection(db, "users", user.uid, "portfolio");
    const unsub = onSnapshot(ref, (snap) => {
      setHoldings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { setError(err.message); setLoading(false); });
    return unsub;
  }, [user]);

  // ── 2. Listen to transactions ─────────────────────────────
  useEffect(() => {
    if (!user) return;
    const ref  = collection(db, "users", user.uid, "transactions");
    const unsub = onSnapshot(ref, (snap) => {
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  // ── 3. Fetch live prices whenever holdings change ─────────
  const refreshPrices = useCallback(async (currentHoldings) => {
    if (!currentHoldings.length) return;
    setPriceLoading(true);
    try {
      const quotes = await fetchBatchQuotes(
        currentHoldings.map((h) => ({ symbol: h.symbol, exchange: h.exchange || "NSE" }))
      );
      const map = {};
      quotes.forEach((q) => { if (q && !q.error) map[q.symbol] = q; });
      setLiveData(map);
    } catch (e) {
      console.error("Price fetch error:", e);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!holdings.length) { setLiveData({}); return; }
    refreshPrices(holdings);
    const iv = setInterval(() => refreshPrices(holdings), 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [holdings, refreshPrices]);

  // ── Computed per-holding metrics ──────────────────────────
  const enrichedHoldings = holdings.map((h) => {
    const quote     = liveData[h.symbol] || {};
    const cmp       = quote.price        || 0;
    const dayChg    = quote.change       || 0;
    const dayChgPct = quote.change_percent || 0;
    const invested  = (h.avg_buy_price || 0) * (h.qty || 0);
    const current   = cmp * (h.qty || 0);
    const pnl       = current - invested;
    const pnlPct    = invested ? (pnl / invested) * 100 : 0;
    const dayPnl    = dayChg * (h.qty || 0);
    return {
      ...h,
      cmp, dayChg, dayChgPct,
      invested, current, pnl, pnlPct, dayPnl,
      name: quote.name || h.symbol,
    };
  });

  // ── Portfolio summary totals ──────────────────────────────
  const summary = enrichedHoldings.reduce(
    (acc, h) => {
      acc.totalInvested += h.invested;
      acc.totalCurrent  += h.current;
      acc.totalDayPnl   += h.dayPnl;
      return acc;
    },
    { totalInvested: 0, totalCurrent: 0, totalDayPnl: 0 }
  );
  summary.totalPnL        = summary.totalCurrent - summary.totalInvested;
  summary.totalPnLPercent = summary.totalInvested
    ? +((summary.totalPnL / summary.totalInvested) * 100).toFixed(2) : 0;
  summary.dayPnLPercent   = summary.totalCurrent
    ? +((summary.totalDayPnl / summary.totalCurrent) * 100).toFixed(2) : 0;

  // ── CRUD: single holding ──────────────────────────────────
  const portfolioRef = () => collection(db, "users", user.uid, "portfolio");
  const txRef        = () => collection(db, "users", user.uid, "transactions");

  async function addHolding(data) {
    // Check if symbol already exists → merge via weighted average
    const existing = holdings.find(
      (h) => h.symbol === data.symbol && h.exchange === (data.exchange || "NSE")
    );
    if (existing) {
      const totalQty = existing.qty + data.qty;
      const avgPrice = ((existing.avg_buy_price * existing.qty) + (data.buy_price * data.qty)) / totalQty;
      await updateDoc(doc(db, "users", user.uid, "portfolio", existing.id), {
        qty:           totalQty,
        avg_buy_price: +avgPrice.toFixed(2),
      });
      return existing.id;
    }
    const docRef = await addDoc(portfolioRef(), {
      symbol:        data.symbol,
      exchange:      data.exchange || "NSE",
      qty:           data.qty,
      avg_buy_price: data.buy_price,
      added_date:    data.date || serverTimestamp(),
      source:        data.source || "manual",
    });
    return docRef.id;
  }

  async function addTransaction(data) {
    // Add to transactions log
    await addDoc(txRef(), {
      symbol:    data.symbol,
      exchange:  data.exchange || "NSE",
      type:      data.type,  // "BUY" | "SELL"
      qty:       data.qty,
      price:     data.price,
      date:      data.date || new Date().toISOString().slice(0, 10),
      brokerage: data.brokerage || 0,
      notes:     data.notes || "",
      source:    data.source || "manual",
      created_at: serverTimestamp(),
    });

    // Update holding
    const existing = holdings.find(
      (h) => h.symbol === data.symbol && h.exchange === (data.exchange || "NSE")
    );

    if (data.type === "BUY") {
      if (existing) {
        const totalQty = existing.qty + data.qty;
        const avgPrice = ((existing.avg_buy_price * existing.qty) + (data.price * data.qty)) / totalQty;
        await updateDoc(doc(db, "users", user.uid, "portfolio", existing.id), {
          qty:           totalQty,
          avg_buy_price: +avgPrice.toFixed(2),
        });
      } else {
        await addDoc(portfolioRef(), {
          symbol:        data.symbol,
          exchange:      data.exchange || "NSE",
          qty:           data.qty,
          avg_buy_price: data.price,
          added_date:    data.date || serverTimestamp(),
          source:        data.source || "manual",
        });
      }
    }

    if (data.type === "SELL" && existing) {
      const remaining = existing.qty - data.qty;
      if (remaining <= 0) {
        await deleteDoc(doc(db, "users", user.uid, "portfolio", existing.id));
      } else {
        await updateDoc(doc(db, "users", user.uid, "portfolio", existing.id), {
          qty: remaining,
        });
      }
    }
  }

  const removeHolding = (id) =>
    deleteDoc(doc(db, "users", user.uid, "portfolio", id));

  const updateHolding = (id, data) =>
    updateDoc(doc(db, "users", user.uid, "portfolio", id), data);

  // ── Batch import from Excel parser ───────────────────────
  // rows: [{ symbol, exchange, qty, buy_price, date, source }]
  async function importHoldings(rows) {
    const batch  = writeBatch(db);
    const errors = [];

    // Group rows by symbol to merge multiple lots
    const grouped = {};
    for (const row of rows) {
      const key = `${row.symbol}__${row.exchange || "NSE"}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    }

    for (const key of Object.keys(grouped)) {
      const lots = grouped[key];
      const sym  = lots[0].symbol;
      const exch = lots[0].exchange || "NSE";

      // Weighted average across all lots
      const totalQty  = lots.reduce((s, l) => s + l.qty, 0);
      const totalCost = lots.reduce((s, l) => s + l.qty * l.buy_price, 0);
      const avgPrice  = totalCost / totalQty;
      const earliest  = lots.map(l => l.date).sort()[0];

      // Check if holding already exists in Firestore
      const existing = holdings.find(
        (h) => h.symbol === sym && h.exchange === exch
      );

      if (existing) {
        // Merge: weighted average with existing
        const mergedQty   = existing.qty + totalQty;
        const mergedAvg   = ((existing.avg_buy_price * existing.qty) + totalCost) / mergedQty;
        const holdRef = doc(db, "users", user.uid, "portfolio", existing.id);
        batch.update(holdRef, {
          qty:           mergedQty,
          avg_buy_price: +mergedAvg.toFixed(2),
        });
      } else {
        const holdRef = doc(collection(db, "users", user.uid, "portfolio"));
        batch.set(holdRef, {
          symbol:        sym,
          exchange:      exch,
          qty:           totalQty,
          avg_buy_price: +avgPrice.toFixed(2),
          added_date:    earliest || new Date().toISOString().slice(0, 10),
          source:        "excel_import",
          import_date:   new Date().toISOString(),
        });
      }

      // Log each lot as a separate transaction
      for (const lot of lots) {
        const txDocRef = doc(collection(db, "users", user.uid, "transactions"));
        batch.set(txDocRef, {
          symbol:     sym,
          exchange:   exch,
          type:       "BUY",
          qty:        lot.qty,
          price:      lot.buy_price,
          date:       lot.date || "",
          brokerage:  lot.brokerage || 0,
          notes:      lot.notes || "",
          source:     "excel_import",
          created_at: new Date().toISOString(),
        });
      }
    }

    await batch.commit();
    return { imported: Object.keys(grouped).length, errors };
  }

  return {
    holdings: enrichedHoldings,
    transactions,
    liveData,
    summary,
    loading,
    priceLoading,
    error,
    addHolding,
    addTransaction,
    removeHolding,
    updateHolding,
    importHoldings,
    refreshPrices: () => refreshPrices(holdings),
  };
}